// LearnCore Frontend - TTS语音朗读服务

import type { TTSVoice, TTSSettings } from '../types';

class TTSService {
  private synth: SpeechSynthesis;
  private voices: TTSVoice[] = [];
  private settings: TTSSettings;
  private initialized = false;
  private voicesReady = false;
  private voicesReadyPromise: Promise<void>;
  private resolveVoicesReady: (() => void) | null = null;

  constructor() {
    this.synth = window.speechSynthesis;
    this.settings = this.getDefaultSettings();
    this.voicesReadyPromise = new Promise<void>((resolve) => {
      this.resolveVoicesReady = resolve;
    });
    this.loadVoices();
    // 如果浏览器已准备好voices，立即标记
    if (this.synth.getVoices().length > 0) {
      this.markVoicesReady();
    }
    
    // 监听voices变化（某些浏览器需要异步加载）
    if (this.synth.onvoiceschanged !== undefined) {
      this.synth.onvoiceschanged = () => {
        this.loadVoices();
        if (this.synth.getVoices().length > 0) {
          this.markVoicesReady();
        }
      };
    }
  }

  // 预初始化：加载设置并等待语音列表可用（带超时容错）
  init(timeoutMs: number = 1500): Promise<void> {
    if (this.initialized) return Promise.resolve();
    this.initialized = true;
    // 加载本地/默认设置
    this.loadSettings();
    // 触发一次获取
    this.loadVoices();
    const existing = this.synth.getVoices();
    if (existing.length > 0) {
      this.markVoicesReady();
      return Promise.resolve();
    }
    // 等待voices或超时
    return Promise.race([
      this.voicesReadyPromise,
      new Promise<void>((resolve) => setTimeout(resolve, timeoutMs))
    ]);
  }

  private getDefaultSettings(): TTSSettings {
    return {
      enabled: true,
      rate: 1.0,
      pitch: 1.0,
      volume: 1.0
    };
  }

  // 将长文本按句子和长度拆分为更小片段，减少启动等待
  private splitTextIntoChunks(text: string, maxLength: number = 220): string[] {
    const normalized = text.replace(/\s+/g, ' ').trim();
    if (normalized.length <= maxLength) return [normalized];

    const sentences = normalized.split(/([。！？!?\.]+)\s*/).reduce<string[]>((acc, cur, idx, arr) => {
      if (!cur) return acc;
      if (/^[。！？!?\.]+$/.test(cur) && acc.length > 0) {
        acc[acc.length - 1] += cur;
      } else if (idx < arr.length - 1 && /^[。！？!?\.]+$/.test(arr[idx + 1] || '')) {
        acc.push(cur); // 等后面的标点合并
      } else if (!/^[。！？!?\.]+$/.test(cur)) {
        acc.push(cur);
      }
      return acc;
    }, []);

    const chunks: string[] = [];
    let buffer = '';
    for (const sentence of sentences) {
      if ((buffer + ' ' + sentence).trim().length <= maxLength) {
        buffer = (buffer ? buffer + ' ' : '') + sentence.trim();
      } else {
        if (buffer) chunks.push(buffer);
        if (sentence.length <= maxLength) {
          buffer = sentence.trim();
        } else {
          // 极长句再按空格硬拆
          let start = 0;
          while (start < sentence.length) {
            const part = sentence.slice(start, start + maxLength);
            chunks.push(part.trim());
            start += maxLength;
          }
          buffer = '';
        }
      }
    }
    if (buffer) chunks.push(buffer);
    return chunks;
  }

  private loadVoices(): void {
    const voices = this.synth.getVoices();
    this.voices = voices.map(voice => ({
      voiceURI: voice.voiceURI,
      name: voice.name,
      lang: voice.lang,
      localService: voice.localService,
      default: voice.default
    }));
  }

  private markVoicesReady(): void {
    if (!this.voicesReady) {
      this.voicesReady = true;
      if (this.resolveVoicesReady) {
        this.resolveVoicesReady();
        this.resolveVoicesReady = null;
      }
    }
  }

  // 获取所有可用的语音
  getAvailableVoices(): TTSVoice[] {
    return this.voices;
  }

  // 获取中文语音
  getChineseVoices(): TTSVoice[] {
    return this.voices.filter(voice => 
      voice.lang.startsWith('zh') || 
      voice.lang.startsWith('cmn') ||
      voice.name.toLowerCase().includes('chinese') ||
      voice.name.toLowerCase().includes('mandarin')
    );
  }

  // 获取英文语音
  getEnglishVoices(): TTSVoice[] {
    return this.voices.filter(voice => 
      voice.lang.startsWith('en') ||
      voice.name.toLowerCase().includes('english')
    );
  }

  // 检测文本语言
  private detectLanguage(text: string): 'chinese' | 'english' | 'unknown' {
    // 简单的语言检测
    const chineseRegex = /[\u4e00-\u9fff]/;
    const englishRegex = /^[a-zA-Z\s.,!?;:'"()-]+$/;
    
    if (chineseRegex.test(text)) {
      return 'chinese';
    } else if (englishRegex.test(text.trim())) {
      return 'english';
    }
    return 'unknown';
  }

  // 获取适合的语音
  private getVoiceForText(text: string): SpeechSynthesisVoice | null {
    const language = this.detectLanguage(text);
    const voices = this.synth.getVoices();
    
    let targetVoiceURI: string | undefined;
    
    if (language === 'chinese' && this.settings.chineseVoice) {
      targetVoiceURI = this.settings.chineseVoice;
    } else if (language === 'english' && this.settings.englishVoice) {
      targetVoiceURI = this.settings.englishVoice;
    }
    
    // 查找指定的语音
    if (targetVoiceURI) {
      const voice = voices.find(v => v.voiceURI === targetVoiceURI);
      if (voice) return voice;
    }
    
    // 回退到默认语音
    if (language === 'chinese') {
      return voices.find(v => 
        v.lang.startsWith('zh') || 
        v.lang.startsWith('cmn')
      ) || null;
    } else if (language === 'english') {
      return voices.find(v => v.lang.startsWith('en')) || null;
    }
    
    return null;
  }

  // 朗读文本
  speak(text: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.settings.enabled) {
        resolve();
        return;
      }

      if (!text.trim()) {
        resolve();
        return;
      }

      // 停止当前播放，并在下一tick开始，避免立即cancel导致的延迟
      this.stop();

      const chunks = this.splitTextIntoChunks(text);
      let index = 0;

      const playNext = () => {
        if (index >= chunks.length) {
          resolve();
          return;
        }
        const current = chunks[index++];
        const utterance = new SpeechSynthesisUtterance(current);
        const voice = this.getVoiceForText(current);
        if (voice) {
          utterance.voice = voice;
          utterance.lang = voice.lang;
        }
        utterance.rate = this.settings.rate;
        utterance.pitch = this.settings.pitch;
        utterance.volume = this.settings.volume;
        utterance.onend = () => {
          // 播放下一段
          playNext();
        };
        utterance.onerror = (event) => {
          reject(new Error('TTS播放失败: ' + (event.error || 'unknown')));
        };
        this.synth.speak(utterance);
      };

      // 在下一事件循环启动，降低首段启动等待
      setTimeout(playNext, 0);
    });
  }

  // 停止朗读
  stop(): void {
    if (this.synth.speaking) {
      this.synth.cancel();
    }
  }

  // 暂停朗读
  pause(): void {
    if (this.synth.speaking) {
      this.synth.pause();
    }
  }

  // 恢复朗读
  resume(): void {
    if (this.synth.paused) {
      this.synth.resume();
    }
  }

  // 检查是否正在朗读
  isSpeaking(): boolean {
    return this.synth.speaking;
  }

  // 检查是否已暂停
  isPaused(): boolean {
    return this.synth.paused;
  }

  // 更新设置
  updateSettings(newSettings: Partial<TTSSettings>): void {
    this.settings = { ...this.settings, ...newSettings };
    this.saveSettings();
  }

  // 获取当前设置
  getSettings(): TTSSettings {
    return { ...this.settings };
  }

  // 保存设置到localStorage
  private saveSettings(): void {
    try {
      localStorage.setItem('tts_settings', JSON.stringify(this.settings));
    } catch (error) {
      console.error('保存TTS设置失败:', error);
    }
  }

  // 从localStorage加载设置
  loadSettings(): void {
    try {
      const saved = localStorage.getItem('tts_settings');
      if (saved) {
        const settings = JSON.parse(saved);
        this.settings = { ...this.getDefaultSettings(), ...settings };
      }
    } catch (error) {
      console.error('加载TTS设置失败:', error);
      this.settings = this.getDefaultSettings();
    }
  }

  // 检查浏览器是否支持TTS
  static isSupported(): boolean {
    return 'speechSynthesis' in window && 'SpeechSynthesisUtterance' in window;
  }
}

// 创建并导出单例实例
export const ttsService = new TTSService();
export { TTSService };
export default ttsService;
